import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import PublicIcon from '@mui/icons-material/Public';
import {
    Alert,
    ListItemIcon,
    Menu,
    MenuItem,
    MenuList,
    Snackbar,
    TextField,
    Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';

import updateGitCommitMessage from '../../services/api/analyses/updateGitCommitMessage';
import updateVisibility from '../../services/api/analyses/updateVisibility';
import useDeleteAnalysis from '../../services/api/analyses/useDeleteAnalysis';
import ConfirmDialog from '../common/ConfirmDialog';

const AnalysisActions = ({
    analysisId,
    projectName,
    refreshAnalysis,
    showOptions,
    anchorEl,
    setAnchorEl,
    actualCommitMessage,
    setActualCommitMessage,
    publicVisibility,
    forcedPublic,
}) => {
    const [isTooLongCommitMessage, setIsTooLongCommitMessage] = useState(false);
    const [renamedCommitMessage, setRenamedCommitMessage] = useState(actualCommitMessage);
    const [isRenamingModalOpened, setIsRenamingModalOpened] = useState(false);
    const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false);
    const [isPublicVisibiliteModalOpened, setIsPublicVisibiliteModalOpened] =
        useState(false);

    const renameAnalysis = async () => {
        if (renamedCommitMessage.length > 254) {
            setIsTooLongCommitMessage(true);
        } else {
            await updateGitCommitMessage(analysisId, renamedCommitMessage);
            await refreshAnalysis();
            setIsRenamingModalOpened(false);
            setActualCommitMessage(renamedCommitMessage);
        }
    };

    const handleVisibilityChange = async (publicVisibility) => {
        await updateVisibility(analysisId, publicVisibility);
        await refreshAnalysis();
    };

    return (
        <>
            <Menu
                open={showOptions}
                anchorEl={anchorEl}
                onClose={() => {
                    setAnchorEl(null);
                }}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        '&:before': {
                            content: '""',
                            position: 'absolute',
                            right: 12,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                        },
                        '& fieldset': {
                            borderColor: 'rgba(0,0,0,0)',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: 'rgba(0,0,0,0)',
                        },
                    },
                }}
            >
                <MenuList>
                    <MenuItem
                        onClick={() => {
                            setIsRenamingModalOpened(true);
                            setAnchorEl(null);
                        }}
                    >
                        <ListItemIcon>
                            <ModeEditIcon />
                        </ListItemIcon>
                        Rename
                    </MenuItem>
                    {!forcedPublic && (
                        <MenuItem
                            onClick={() => {
                                setIsPublicVisibiliteModalOpened(true);
                                setAnchorEl(null);
                            }}
                        >
                            <ListItemIcon>
                                {publicVisibility ? <LockIcon /> : <PublicIcon />}
                            </ListItemIcon>
                            Make {publicVisibility ? 'private' : 'public'}
                        </MenuItem>
                    )}
                    <MenuItem
                        onClick={() => {
                            setIsDeleteModalOpened(true);
                            setAnchorEl(null);
                        }}
                    >
                        <ListItemIcon>
                            <DeleteIcon color="red" />
                        </ListItemIcon>
                        <Typography color="red" sx={{ color: '#ff0000' }}>
                            Delete
                        </Typography>
                    </MenuItem>
                </MenuList>
            </Menu>

            <ConfirmDialog
                open={isRenamingModalOpened}
                title="Rename analysis"
                onValidate={renameAnalysis}
                onCancel={() => setIsRenamingModalOpened(false)}
                validationText="Save"
                mainSentence={
                    <TextField
                        sx={{ mt: 1 }}
                        label="Name"
                        fullWidth
                        size="small"
                        color="dark"
                        value={renamedCommitMessage}
                        onChange={(event) => {
                            setRenamedCommitMessage(event.target.value);
                        }}
                    />
                }
                softDisplay
            />
            <ConfirmDialog
                open={isPublicVisibiliteModalOpened}
                title={`Make analysis ${publicVisibility ? 'private' : 'public'}`}
                onValidate={() => {
                    handleVisibilityChange(!publicVisibility);
                    setIsPublicVisibiliteModalOpened(false);
                }}
                onCancel={() => setIsPublicVisibiliteModalOpened(false)}
                validationText={`Make it ${publicVisibility ? 'private' : 'public'}`}
                mainSentence={
                    <>
                        {`Are you sure you want to do this ? People outside of your organization will ${
                            publicVisibility ? 'no longer' : ''
                        } be able to see it.`}
                    </>
                }
                softDisplay
            />
            <ConfirmDialog
                open={isDeleteModalOpened}
                title="Delete of analysis"
                onValidate={() => {
                    useDeleteAnalysis(analysisId);
                    setIsDeleteModalOpened(false);
                }}
                onCancel={() => setIsDeleteModalOpened(false)}
                validationText={<a href={`/projects/${projectName}`}>Confirm</a>}
                mainSentence={
                    <>
                        Are you sure you want to permanently delete this analysis, with no
                        possible recovery?
                    </>
                }
            />
            <Snackbar
                open={isTooLongCommitMessage}
                autoHideDuration={6000}
                onClose={() => setIsTooLongCommitMessage(false)}
            >
                <Alert severity="error">The new name is too long</Alert>
            </Snackbar>
        </>
    );
};

AnalysisActions.propTypes = {
    analysisId: PropTypes.string,
    projectName: PropTypes.string,
    refreshAnalysis: PropTypes.func,
    showOptions: PropTypes.bool,
    anchorEl: PropTypes.any,
    setAnchorEl: PropTypes.func,
    actualCommitMessage: PropTypes.string,
    setActualCommitMessage: PropTypes.func,
    publicVisibility: PropTypes.bool,
    forcedPublic: PropTypes.bool,
};

export default AnalysisActions;
